import React, { useEffect, useState } from 'react';
import { Box, Drawer, Typography, Checkbox } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { toast } from 'react-toastify';
import { Bone, fadeInSx } from '../../../../components/ui/DashboardSkeletons';
import { API } from '../../../../api';
import DosenButton from '../../../../components/ui/Buttons/DosenButton';
import { MOBILE_SX } from '../../../../mobile/mobileApp';

interface OnboardingFieldsDrawerProps {
  open: boolean;
  onClose: () => void;
  authId: string;
}

interface OrgField {
  key: string;
  required: boolean;
}

// Nicer labels for a few keys; the rest fall back to a camelCase humanizer.
const LABEL_OVERRIDES: Record<string, string> = {
  category: 'User Role',
  timezone: 'Time Zone',
  bio: 'Biography',
  headshot: 'Profile Photo',
  dob: 'DOB',
  country: 'Country',
  workHistory: 'Work History',
  educationHistory: 'Education History',
  hobbiesInterests: 'Hobbies & interests',
  socials: 'Socials',
  department: 'Department',
  companyName: 'Company Name',
  companyInfo: 'Company Information',
  companyPosition: 'Company Position'
};

// Curated onboarding fields grouped for the drawer. Mirrors the BE
// AVAILABLE_ONBOARDING_FIELDS (orgOnboardingFields/index.ts). Only fields the BE
// returns in `availableFields` are rendered, in this order / grouping.
const FIELD_GROUPS: Array<{ title: string; keys: string[] }> = [
  { title: 'Basic', keys: ['firstName', 'lastName', 'email', 'category', 'timezone'] },
  { title: 'Demographic', keys: ['bio', 'headshot', 'dob', 'country'] },
  {
    title: 'Personal, Career and Studies',
    keys: ['workHistory', 'educationHistory', 'hobbiesInterests', 'socials', 'department']
  },
  { title: 'Company', keys: ['companyName', 'companyInfo', 'companyPosition'] }
];

export const humanize = (key: string): string =>
  LABEL_OVERRIDES[key] ||
  key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();

// The Include and Required columns; narrower on a phone so a field's name keeps its line.
const CHECK_COL = { width: 80, textAlign: 'center' as const, [MOBILE_SX]: { width: 60, flexShrink: 0 } };

/**
 * Org-level onboarding fields config (flat list — replaces the per-program,
 * per-role config). Admin picks which fields are part of onboarding and which
 * are required. Mandatory fields are always on + required (locked). Consumes
 * GET/PUT /org/{authId}/onboarding-fields (BE-5).
 */
const OnboardingFieldsDrawer = ({ open, onClose, authId }: OnboardingFieldsDrawerProps) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [available, setAvailable] = useState<string[]>([]);
  const [mandatory, setMandatory] = useState<Set<string>>(new Set());
  const [included, setIncluded] = useState<Record<string, boolean>>({});
  const [required, setRequired] = useState<Record<string, boolean>>({});
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const toggleGroup = (title: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });

  useEffect(() => {
    if (!open || !authId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await API.getOrgOnboardingFields(authId);
        const data = res?.data || {};
        const avail: string[] = Array.isArray(data.availableFields) ? data.availableFields : [];
        const mand: string[] = Array.isArray(data.mandatoryFields) ? data.mandatoryFields : [];
        const configured: OrgField[] = Array.isArray(data.onboardingFields) ? data.onboardingFields : [];
        const mandSet = new Set(mand);
        const inc: Record<string, boolean> = {};
        const req: Record<string, boolean> = {};
        const byKey = new Map(configured.map((f) => [f.key, f]));
        avail.forEach((k) => {
          const isMand = mandSet.has(k);
          const cfg = byKey.get(k);
          inc[k] = isMand || Boolean(cfg);
          req[k] = isMand || Boolean(cfg?.required);
        });
        if (!cancelled) {
          setAvailable(avail);
          setMandatory(mandSet);
          setIncluded(inc);
          setRequired(req);
        }
      } catch {
        if (!cancelled) toast.error('Onboarding fields could not be loaded. Refresh the page and try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, authId]);

  const toggleInclude = (key: string) => {
    if (mandatory.has(key)) return;
    setIncluded((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      if (!next[key]) setRequired((r) => ({ ...r, [key]: false }));
      return next;
    });
  };

  const toggleRequired = (key: string) => {
    if (mandatory.has(key) || !included[key]) return;
    setRequired((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: OrgField[] = available
        .filter((k) => included[k])
        .map((k) => ({ key: k, required: mandatory.has(k) ? true : Boolean(required[k]) }));
      await API.updateOrgOnboardingFields(authId, payload);
      toast.success('Onboarding fields saved');
      onClose();
    } catch {
      toast.error('Onboarding fields could not be saved. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const rowText = { fontSize: 14, color: '#FDFDFD', fontFamily: 'Inter, sans-serif' };
  const colHeader = {
    fontSize: 11,
    fontWeight: 600,
    color: '#868686',
    textTransform: 'uppercase' as const,
    fontFamily: 'Inter, sans-serif'
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: 460, bgcolor: '#0F0F0F', borderLeft: '1px solid #2A2A2A', [MOBILE_SX]: { width: '100%' } }
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            borderBottom: '1px solid #2A2A2A'
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#FDFDFD', fontFamily: 'Inter, sans-serif' }}>
              Onboarding fields
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#868686', fontFamily: 'Inter, sans-serif' }}>
              Fields new people complete when joining the organization
            </Typography>
          </Box>
          <CloseIcon onClick={onClose} sx={{ color: '#868686', cursor: 'pointer' }} />
        </Box>

        {/* Body */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2, [MOBILE_SX]: { px: 1 } }}>
          {loading ? (
            <Box
              aria-busy
              aria-label="Loading"
              sx={{ display: 'flex', flexDirection: 'column', gap: '12px', px: 1, py: 1 }}
            >
              {Array.from({ length: 8 }, (_, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Bone width="50%" height={14} />
                  <Box sx={{ flex: 1 }} />
                  <Bone width={18} height={18} radius="4px" />
                  <Bone width={18} height={18} radius="4px" sx={{ mx: '31px' }} />
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={fadeInSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={colHeader}>Field</Typography>
                </Box>
                <Box sx={CHECK_COL}>
                  <Typography sx={colHeader}>Include</Typography>
                </Box>
                <Box sx={CHECK_COL}>
                  <Typography sx={colHeader}>Required</Typography>
                </Box>
              </Box>
              {FIELD_GROUPS.map((group) => {
                const keys = group.keys.filter((k) => available.includes(k));
                if (keys.length === 0) return null;
                const isCollapsed = collapsed.has(group.title);
                return (
                  <Box key={group.title}>
                    <Box
                      onClick={() => toggleGroup(group.title)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        cursor: 'pointer',
                        px: 1,
                        py: 1.25,
                        borderTop: '1px solid #1C1C1C'
                      }}
                    >
                      <ExpandMoreIcon
                        sx={{
                          color: '#868686',
                          fontSize: 18,
                          transform: isCollapsed ? 'rotate(-90deg)' : 'none',
                          transition: 'transform .15s'
                        }}
                      />
                      <Typography
                        sx={{ fontSize: 12, fontWeight: 700, color: '#FDFDFD', fontFamily: 'Inter, sans-serif' }}
                      >
                        {group.title}
                      </Typography>
                    </Box>
                    {!isCollapsed &&
                      keys.map((key) => {
                        const isMand = mandatory.has(key);
                        return (
                          <Box key={key} sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5 }}>
                            <Box
                              sx={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                pl: 3,
                                // A phone: "(mandatory)" drops under the name before the name breaks.
                                [MOBILE_SX]: { pl: 1.5, minWidth: 0, flexWrap: 'wrap', rowGap: 0 }
                              }}
                            >
                              <Typography sx={rowText}>{humanize(key)}</Typography>
                              {isMand && (
                                <Typography
                                  sx={{
                                    fontSize: 10,
                                    color: '#868686',
                                    fontFamily: 'Inter, sans-serif',
                                    [MOBILE_SX]: { whiteSpace: 'nowrap' }
                                  }}
                                >
                                  (mandatory)
                                </Typography>
                              )}
                            </Box>
                            <Box sx={CHECK_COL}>
                              <Checkbox
                                checked={Boolean(included[key])}
                                disabled={isMand}
                                onChange={() => toggleInclude(key)}
                                sx={{ color: '#565656', '&.Mui-checked': { color: '#F79420' } }}
                              />
                            </Box>
                            <Box sx={CHECK_COL}>
                              <Checkbox
                                checked={Boolean(required[key])}
                                disabled={isMand || !included[key]}
                                onChange={() => toggleRequired(key)}
                                sx={{ color: '#565656', '&.Mui-checked': { color: '#F79420' } }}
                              />
                            </Box>
                          </Box>
                        );
                      })}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ px: 3, py: 2, borderTop: '1px solid #2A2A2A', display: 'flex', justifyContent: 'flex-end' }}>
          <DosenButton
            tone="cta"
            onClick={handleSave}
            disabled={saving || loading}
            loading={saving}
            loadingText="Saving..."
          >
            Save
          </DosenButton>
        </Box>
      </Box>
    </Drawer>
  );
};

export default OnboardingFieldsDrawer;
